import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary: [
    'bg-primary text-primary-foreground',
    'shadow-[0_1px_2px_rgb(0_0_0/0.15),inset_0_1px_0_rgb(255_255_255/0.15)]',
    'hover:brightness-110 active:brightness-95 active:scale-[0.98]',
  ].join(' '),
  secondary:
    'bg-secondary text-secondary-foreground border border-border hover:bg-accent active:scale-[0.98]',
  ghost: 'text-foreground hover:bg-accent active:bg-accent/70 active:scale-[0.98]',
  danger: [
    'bg-destructive text-destructive-foreground',
    'shadow-[0_1px_2px_rgb(0_0_0/0.15)]',
    'hover:brightness-110 active:brightness-95 active:scale-[0.98]',
  ].join(' '),
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-base gap-2',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      // `aria-busy` para leitor de tela: o spinner sozinho não comunica nada.
      aria-busy={loading}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium',
        'transition-all focus-visible:outline-none focus-visible:shadow-glow',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}
